import { useMemo, useState } from 'react';
import { useAdminPublishedApps, useTakedownPublishedApp } from '../useAdmin';
import type { AdminPublishedAppRow, ListPublishedAppsParams } from '../adminApi';
import { AuthApiError } from '../../account/authApi';
import './AdminPublishedApps.css';

const PAGE_SIZE = 25;

/**
 * Admin published-apps oversight (F10). The kid-published mini-apps
 * served at bryta.app/p/<short_uid>/<slug>. Per-row takedown for
 * legal / abuse complaints.
 */
export function AdminPublishedApps(): JSX.Element {
  const [q, setQ] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [page, setPage] = useState(0);

  const params = useMemo<ListPublishedAppsParams>(
    () => ({
      q: q.trim() || undefined,
      user_email: userEmail.trim() || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [q, userEmail, page],
  );

  const { data, isLoading, isError, error, isFetching } = useAdminPublishedApps(params);
  const takedown = useTakedownPublishedApp();

  const total = data?.total ?? 0;
  const showing = data?.rows.length ?? 0;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex = page * PAGE_SIZE + showing;
  const canPrev = page > 0;
  const canNext = (page + 1) * PAGE_SIZE < total;

  function reset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(0);
    };
  }

  return (
    <section className="bl-admin-pub" aria-label="Published apps oversight">
      <header className="bl-admin-pub__header">
        <div>
          <h2>Published apps</h2>
          <p className="bl-admin-pub__sub">
            Mini-apps the kids have shipped to bryta.app. Take down for legal or safety reasons; the
            URL stops resolving immediately.
          </p>
        </div>
        <p className="bl-admin-pub__count" aria-live="polite">
          {isLoading
            ? 'Loading…'
            : total === 0
              ? 'No apps match'
              : `${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${total.toLocaleString()}`}
          {isFetching && !isLoading && ' · refreshing…'}
        </p>
      </header>

      <form className="bl-admin-pub__filters" onSubmit={(e) => e.preventDefault()} role="search">
        <label className="bl-admin-pub__field">
          <span className="bl-admin-pub__field-label">Title or slug contains</span>
          <input
            type="search"
            value={q}
            onChange={(e) => reset(setQ)(e.target.value)}
            placeholder="birds or my-app"
            autoComplete="off"
          />
        </label>
        <label className="bl-admin-pub__field">
          <span className="bl-admin-pub__field-label">Owner email</span>
          <input
            type="search"
            value={userEmail}
            onChange={(e) => reset(setUserEmail)(e.target.value)}
            placeholder="ada@school.edu"
            autoComplete="off"
          />
        </label>
      </form>

      {(isError || takedown.error) && (
        <p className="bl-admin-pub__error" role="alert">
          {takedown.error instanceof AuthApiError
            ? takedown.error.detail || takedown.error.message
            : (error || takedown.error) instanceof Error
              ? ((error || takedown.error) as Error).message
              : 'Could not complete the request.'}
        </p>
      )}

      <div className="bl-admin-pub__table-wrap">
        <table className="bl-admin-pub__table">
          <thead>
            <tr>
              <th scope="col">Title</th>
              <th scope="col">Slug</th>
              <th scope="col">Owner</th>
              <th scope="col" className="bl-admin-pub__num">
                Views
              </th>
              <th scope="col" className="bl-admin-pub__num">
                v
              </th>
              <th scope="col">Published</th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((row) => (
              <PublishedAppRow
                key={row.id}
                row={row}
                onTakedown={() => {
                  const reason = window.prompt(`Reason for taking down "${row.title}" (logged):`);
                  if (!reason || !reason.trim()) return;
                  takedown.mutate({ id: row.id, reason: reason.trim() });
                }}
                busy={takedown.isPending}
              />
            ))}
            {!isLoading && data?.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="bl-admin-pub__empty">
                  No published apps match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <nav className="bl-admin-pub__pagination" aria-label="Pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
        >
          ← Previous
        </button>
        <span className="bl-admin-pub__page-label">
          Page {page + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / PAGE_SIZE))}`}
        </span>
        <button type="button" onClick={() => setPage((p) => p + 1)} disabled={!canNext}>
          Next →
        </button>
      </nav>
    </section>
  );
}

function PublishedAppRow({
  row,
  onTakedown,
  busy,
}: {
  row: AdminPublishedAppRow;
  onTakedown: () => void;
  busy: boolean;
}): JSX.Element {
  return (
    <tr>
      <td>
        <a
          href={row.share_url}
          target="_blank"
          rel="noopener noreferrer"
          className="bl-admin-pub__title-link"
          title={`Open ${row.share_url} in a new tab`}
        >
          {row.title}
          <span aria-hidden="true" className="bl-admin-pub__ext">
            ↗
          </span>
        </a>
      </td>
      <td>
        <code className="bl-admin-pub__slug">{row.slug}</code>
      </td>
      <td>{row.user_email || '(unknown)'}</td>
      <td className="bl-admin-pub__num">{row.view_count.toLocaleString()}</td>
      <td className="bl-admin-pub__num">{row.version}</td>
      <td>
        <time dateTime={row.created_at}>{formatDate(row.created_at)}</time>
      </td>
      <td>
        <div className="bl-admin-pub__row-actions">
          <a
            href={row.share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bl-admin-pub__row-link"
          >
            Open
          </a>
          <button
            type="button"
            onClick={onTakedown}
            disabled={busy}
            className="bl-admin-pub__row-action--danger"
          >
            Take down
          </button>
        </div>
      </td>
    </tr>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
